import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { t } from "ttag";

import { constants, reverse } from "@reactivated";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import * as productAPI from "../../../api/products";
import { Image } from "../../../components/Image";
import { Link } from "../../../components/Link";
import { RatingGraphic } from "../../../components/RatingGraphic";
import { RawHTML } from "../../../components/RawHTML";
import { IProduct, IProductAttributes } from "../../../models/catalogue";
import {
    IProductAPIURL,
    IProductID,
    isoWebPageURL,
} from "../../../models/nominals";
import { IAPIPrice } from "../../../models/prices";
// Basket ops come from the shared store layer, not apps/checkout. This still
// reaches checkout transitively (the thunk calls checkout's `loadBasket`);
// see the note in store/basket/thunks/index.ts.
import { addBasketLine } from "../../../store/basket/thunks";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { getProductVariant } from "../../../store/selectors";
import { trackAddToBasketEvent } from "../../../utils/analytics";
import {
    PriceType,
    getActivePromoCallout,
    getOptionValueDisplayName,
    getProductPrice,
    isProductPriceDiscounted,
} from "../../../utils/catalogue";
import { isMobile } from "../../../utils/detectMobile";
import { formatTermLength, money } from "../../../utils/format";
import { getMonthlyPrice } from "../../../utils/money";
import { usePageSetting, useShouldShowPricing } from "../../../utils/settings";
import {
    SortType,
    getCheapestProductVariant,
    sortProductVariants,
} from "../../../utils/sorting";
import { ProductCardConfigurator } from "../ProductCardConfigurator";
import { defaults } from "../defaults";
import { GridVariant, IProductCardType } from "../models";
import { blurProduct, focusProduct } from "../reducers";
import { GRID_TILE_CLASS } from "../tiles";

import styles from "./index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

export const enum CartButtonStatus {
    INITIAL,
    ADDING,
    ADDED,
}

interface ICardPortionProps {
    gridVariant: GridVariant;
    cardType: IProductCardType;
    rootProduct: IProduct;
    variant: IProduct | null;
    customStyle?: string;
    hideRatings?: boolean;
}

export const CardTitle = (props: ICardPortionProps) => {
    const titleClass = classNames({
        [styles.title]: true,
        [`${props.cardType}__title`]: true,
        "product-grid-card__title--no-pre":
            !props.rootProduct.attributes.product_callout,
        [`${props.cardType}__title--no-pre`]:
            !props.rootProduct.attributes.product_callout,
    });
    return <h3 className={titleClass}>{props.rootProduct.title}</h3>;
};

export const PillowSleepBanner = (props: ICardPortionProps) => {
    if (props.gridVariant !== "pillows") {
        return null;
    }
    const sleepPositions: Array<keyof IProductAttributes> = [
        "back_sleeper",
        "side_sleeper",
        "stomach_sleeper",
    ];
    const positionNames = sleepPositions
        .filter((position) => {
            const attr = props.rootProduct.attributes[position];
            return attr ? attr.value : false;
        })
        .map((position) => {
            const attr = props.rootProduct.attributes[position];
            return attr ? attr.name.split(" ")[0] : "";
        });
    let sleepPosition: string | null = null;
    if (positionNames.length === 1) {
        sleepPosition = interpolate(t`%s sleepers`, positionNames);
    } else if (positionNames.length === 2) {
        sleepPosition = interpolate(t`%s and %s sleepers`, positionNames);
    } else if (positionNames.length === 3) {
        sleepPosition = interpolate(t`%s, %s, and %s sleepers`, positionNames);
    }
    if (!sleepPosition) {
        return null;
    }
    return <div className={styles.badge}>{sleepPosition}</div>;
};

export const CardImage = (props: ICardPortionProps) => {
    if (props.rootProduct.images.length <= 0) {
        return null;
    }
    const imageClass = classNames({
        ["responsive-img"]: true,
        [styles.img]: true,
        [`${props.cardType}__image`]: true,
        [`${props.cardType}--${props.gridVariant}__image`]: true,
    });
    const image =
        props.rootProduct.images.length > 1
            ? props.rootProduct.images.find(
                  (img) => img.role === "Product Tile",
              )
            : props.rootProduct.images[0];
    if (!image) {
        return null;
    }
    return (
        <Image
            src={image.original}
            className={imageClass}
            alt={props.rootProduct.title}
        />
    );
};

export const CardPromo = (props: ICardPortionProps) => {
    if (!props.rootProduct.attributes.promo_badge) {
        return null;
    }
    return (
        <div className={styles.promo}>
            <div className={styles.middleCircle}>
                <div className={styles.innerCircle}>
                    <span className={styles.promoCopy}>
                        {`${props.rootProduct.attributes.promo_badge.value}`}
                    </span>
                </div>
            </div>
        </div>
    );
};

export const CardCallout = (props: ICardPortionProps) => {
    // TEMPORARY: getActivePromoCallout handles both old string format and new array format
    // This ensures graceful handling during the cache transition period
    const activePromoCallout = getActivePromoCallout(
        props.rootProduct.promo_callout,
    );
    const promo_callout = activePromoCallout
        ? activePromoCallout
        : props.rootProduct.attributes.product_callout?.value;
    if (!promo_callout) {
        return null;
    }
    return (
        <div className={styles.callout}>
            <span>{promo_callout}</span>
        </div>
    );
};

export const CardProductLink = (
    props: ICardPortionProps & {
        children: React.ReactNode;
        hashFragment?: string;
    },
) => {
    const linkTitle = interpolate(t`Learn more about %s`, [
        props.rootProduct.title,
    ]);
    let url = props.variant ? props.variant.link : props.rootProduct.link;
    if (props.hashFragment) {
        if (!url) {
            url = isoWebPageURL.wrap("");
        }
        url = isoWebPageURL.wrap(
            `${isoWebPageURL.unwrap(url)}#${props.hashFragment}`,
        );
    }
    return (
        <Link href={url} title={linkTitle} className={props.customStyle}>
            {props.children}
        </Link>
    );
};

export const CardRating = (props: ICardPortionProps) => {
    if (!props.rootProduct.rating || props.hideRatings) {
        return null;
    }
    return (
        <CardProductLink {...props} hashFragment={"reviews"}>
            <RatingGraphic
                rating={props.rootProduct.rating}
                numReviews={props.rootProduct.num_reviews}
                onlyReviewsNum={false}
                cardClass={props.cardType}
                cardSize={"small"}
            />
        </CardProductLink>
    );
};

export const CardCopy = (props: ICardPortionProps) => {
    const copyClass = classNames({
        [styles.copy]: true,
        [`${props.cardType}__copy`]: true,
        [`${props.cardType}--${props.gridVariant}__copy`]: true,
    });
    return (
        <RawHTML className={copyClass} html={props.rootProduct.description} />
    );
};

export const CardFeelBadge = (props: ICardPortionProps) => {
    const feelClass = classNames({
        [`${props.cardType}__feel-badge`]: true,
    });
    const rootFeel = props.rootProduct.attributes.feel;
    let feel = rootFeel ? getOptionValueDisplayName(rootFeel) : undefined;
    if (!feel) {
        const sortedChildren = sortProductVariants(
            props.rootProduct,
            SortType.PRESELECT,
        );
        for (const child of sortedChildren) {
            const childFeel = child.attributes.feel;
            if (!feel && childFeel) {
                feel = getOptionValueDisplayName(childFeel);
            }
        }
    }
    if (!feel) {
        return null;
    }
    return <div className={feelClass}>{interpolate(t`FEEL: %s`, [feel])}</div>;
};

interface ICardPriceContentProps extends ICardPortionProps {
    price: IAPIPrice | null;
    quantity: number;
}

const CardPriceContent = (props: ICardPriceContentProps) => {
    if (!props.variant) {
        return null;
    }
    const priceWrapperClass = classNames({
        [styles.priceContainer]: true,
        [`${props.cardType}__price`]: true,
        [`${props.cardType}--${props.gridVariant}__price`]: true,
    });
    const qtyAdjustedRetailPrice = getProductPrice(
        props.price ?? props.variant.price,
        {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: props.quantity,
        },
    );
    const qtyAdjustedActualPrice = getProductPrice(
        props.price ?? props.variant.price,
        {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: props.quantity,
        },
    );
    const isDiscounted =
        props.price && isProductPriceDiscounted(props.price.total);
    const regularPriceClass = classNames({
        ["product-grid-card__price__actual"]: true,
        [`grid-card-configurator__price__actual--${props.gridVariant}`]: true,
        [styles.priceRegular]: true,
        [styles.priceActualHighlight]: isDiscounted,
    });
    const priceCopy =
        props.rootProduct.children.length > 0 ? (
            <span className={styles.priceCopy}>{t`Starting at`}</span>
        ) : null;
    if (
        !props.variant.availability.is_available_to_buy ||
        !qtyAdjustedActualPrice
    ) {
        return (
            <div className={priceWrapperClass}>
                <span className={styles.priceUnavilable}>{t`Unavailable`}</span>
            </div>
        );
    }
    if (!isDiscounted) {
        return (
            <div className={priceWrapperClass}>
                {priceCopy}
                <CardProductLink {...props}>
                    <span className="product-grid-card__regular-price">
                        <span className={regularPriceClass}>
                            {money(qtyAdjustedActualPrice)}
                        </span>
                    </span>
                </CardProductLink>
            </div>
        );
    }
    return (
        <>
            <div className={priceWrapperClass}>
                {priceCopy}
                <CardProductLink {...props}>
                    <span className="product-grid-card__sale-price">
                        <span className={styles.priceRetail}>
                            {money(qtyAdjustedRetailPrice)}
                        </span>
                        <span className={regularPriceClass}>
                            {money(qtyAdjustedActualPrice)}
                        </span>
                    </span>
                </CardProductLink>
            </div>
            {props.variant.price.enable_per_month_pricing &&
                props.variant.price.per_month_term_length && (
                    <a
                        href="https://www.tempurpedicoutlet.com/finance-your-purchase/#terms_and_conditions"
                        className={styles.financing}
                    >
                        {(() => {
                            const monthlyPrice = money(
                                getMonthlyPrice(
                                    qtyAdjustedActualPrice,
                                    props.variant.price.per_month_term_length,
                                ),
                            );
                            const termLength = formatTermLength(
                                props.variant.price.per_month_term_length,
                            );
                            const totalPayments = money(qtyAdjustedActualPrice);
                            return richT(
                                t`Or <monthlyPrice>${monthlyPrice}</monthlyPrice>/mo<sup>2</sup> for <months>${termLength}</months> months<sup>1</sup> (total payments of ${totalPayments})`,
                                {
                                    monthlyPrice: (children) => (
                                        <span
                                            className={
                                                styles.financingUnderline
                                            }
                                        >
                                            {children}
                                        </span>
                                    ),
                                    months: (children) => (
                                        <span
                                            className={
                                                styles.financingUnderline
                                            }
                                        >
                                            {children}
                                        </span>
                                    ),
                                },
                            );
                        })()}
                    </a>
                )}
        </>
    );
};

interface ICardPriceProps extends ICardPortionProps {
    quantity: number;
}

export const CardPrice = (props: ICardPriceProps): React.ReactNode => {
    const [price, setPrice] = useState<IAPIPrice | null>(null);

    useEffect(() => {
        const loadPrice = async () => {
            try {
                // If no product variant selected, abort.
                if (!props.variant) {
                    setPrice(null);
                    return;
                }
                // If the quantity is 1, use the default price from the product API.
                if (props.quantity === 1) {
                    setPrice(null);
                    return;
                }
                // If the price for this product/quantity is already loaded, abort
                if (
                    props.variant.url === price?.product &&
                    props.quantity === price?.quantity
                ) {
                    return;
                }
                const newPrice = await productAPI.getProductPrice(
                    props.variant.url,
                    props.quantity,
                );
                setPrice(newPrice);
            } catch (e) {
                setPrice(null);
            }
        };

        loadPrice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.variant?.url, props.quantity]);

    return (
        <CardPriceContent {...props} price={price} quantity={props.quantity} />
    );
};

export const CardDefaultPrice = (props: ICardPortionProps) => {
    props = { ...props };
    if (!props.variant) {
        props.variant = props.rootProduct.availability.is_available_to_buy
            ? props.rootProduct
            : getCheapestProductVariant(props.rootProduct);
    }
    return <CardPrice {...props} quantity={1} />;
};

export const CardHoverPrice = (
    props: ICardPortionProps & { quantity: number },
) => {
    return <CardPrice {...props} />;
};

interface ICardAddToCartButtonProps extends ICardPortionProps {
    quantity: number;
    buttonStatus: CartButtonStatus;
    onAddToBasket: () => void;
}

export const CardAddToCartButton = (props: ICardAddToCartButtonProps) => {
    const buttonClass = classNames({
        "button": true,
        "button--primary-congress-blue": true,
        [`${props.cardType}__button`]: true,
        [`${props.cardType}__button--${props.variant?.id}`]: true,
        [`al-${props.cardType}__button`]: true,
        [`al-${props.cardType}__button--add-to-cart`]: true,
    });
    if (!props.variant || !props.variant.availability.is_available_to_buy) {
        return (
            <div>
                <br />
                <div className="product-grid-card__unavailable">
                    {props.variant ? props.variant.availability.message : ""}
                </div>
                <button className={buttonClass} disabled={true}>
                    {t`UNAVAILABLE`}
                </button>
            </div>
        );
    }
    return (
        <button
            className={buttonClass}
            onClick={props.onAddToBasket}
            disabled={props.buttonStatus !== CartButtonStatus.INITIAL}
        >
            {t`Add to Cart`}
        </button>
    );
};

export const CardViewCartButton = (props: ICardPortionProps) => {
    const buttonClass = classNames({
        "button": true,
        "button--primary-congress-blue": true,
        [`${props.cardType}__button`]: true,
        [`${props.cardType}__button--${props.variant?.id}`]: true,
        [`al-${props.cardType}__button`]: true,
        [`al-${props.cardType}__button--go-to-cart`]: true,
    });
    return (
        <Link
            className={buttonClass}
            href={isoWebPageURL.wrap(reverse("basket:summary"))}
        >
            {t`Go to Cart`}
        </Link>
    );
};

export const CardFullDetailsLink = (props: ICardPortionProps) => {
    const wrapperClass = classNames({
        [`${props.cardType}__full-details`]: true,
    });
    return (
        <div className={wrapperClass}>
            <CardProductLink {...props}>{t`SEE FULL DETAILS`}</CardProductLink>
        </div>
    );
};

interface ICardHoverOverlayProps extends ICardPortionProps {
    quantity: number;
    isOpen: boolean;
    isMobile: boolean;
    buttonStatus: CartButtonStatus;
    onAddToBasket: () => void;
}

export const CardHoverOverlay = (props: ICardHoverOverlayProps) => {
    const overlayClass = classNames({
        [`${props.cardType}__hover-state`]: true,
        [`${props.cardType}__hover-state--visible`]: props.isOpen,
        [`${props.cardType}__hover-state--display-none`]: props.isMobile,
    });
    const contentClass = classNames({
        [`${props.cardType}--${props.gridVariant}__content`]: true,
        [`${props.cardType}__hover-state__content`]: true,
    });
    return (
        <div className={overlayClass}>
            <PillowSleepBanner {...props} />
            <CardProductLink {...props}>
                <CardImage {...props} />
                <CardPromo {...props} />
            </CardProductLink>
            <div className={contentClass}>
                <CardProductLink {...props}>
                    <CardCallout {...props} />
                    <CardTitle {...props} />
                </CardProductLink>
                <CardRating {...props} />
                <CardProductLink {...props}>
                    <CardCopy {...props} />
                </CardProductLink>
                <CardFeelBadge {...props} />
                <ProductCardConfigurator rootProduct={props.rootProduct} />
                <CardHoverPrice {...props} />
                {/* financing*/}
                {props.buttonStatus === CartButtonStatus.ADDED ? (
                    <CardViewCartButton {...props} />
                ) : (
                    <CardAddToCartButton {...props} />
                )}
                <CardFullDetailsLink {...props} />
            </div>
        </div>
    );
};

interface IProductCardProps {
    gridVariant: GridVariant;
    cardType: IProductCardType;
    rootProduct: IProduct;
    disableHoverOverlay?: boolean;
    buildExternalCallout?: (rootProduct: IProduct) => React.ReactNode;
    forwardedRef?: React.RefObject<HTMLDivElement | null>;
}

export const ProductCard = React.memo(
    (props: IProductCardProps): React.ReactNode => {
        const shouldShowPricing = useShouldShowPricing();
        const appSlug = usePageSetting("app_slug") || "";
        const hideRatings = appSlug === AppSlug.TEMPURCA;
        const dispatch = useAppDispatch();

        // Redux state
        const state = useAppSelector(
            (rootState) => rootState.productgrid2 || defaults,
        );
        const isFocused = state.focusedProductID === props.rootProduct.id;
        const variant = getProductVariant(
            [props.rootProduct],
            state.optionValues,
        );
        const quantity = state.quantity;

        // Local state
        const [buttonStatus, setButtonStatus] = useState<CartButtonStatus>(
            CartButtonStatus.INITIAL,
        );

        // Handlers
        const onFocus = (productID: IProductID) => {
            dispatch(focusProduct(productID));
        };

        const onBlur = (productID: IProductID) => {
            dispatch(blurProduct(productID));
        };

        const onAddToBasket = async (
            productURL: IProductAPIURL,
            qty: number,
        ) => {
            await dispatch(addBasketLine({ productURL, qty })).unwrap();
        };

        const handleHoverOverlayOpen = () => {
            if (props.disableHoverOverlay || isFocused) {
                return;
            }
            onFocus(props.rootProduct.id);
        };

        const handleHoverOverlayClose = (
            event: React.MouseEvent<HTMLElement>,
        ) => {
            // Firefox will emit onMouseLeave events when hoving AND clicking on a select box
            // However we don't want to toggle the hover state just by hoving or clicking the select box
            if (
                (event.target as HTMLElement).tagName === "SELECT" ||
                event.currentTarget.tagName === "SELECT"
            ) {
                return;
            }
            onBlur(props.rootProduct.id);
        };

        const handleAddToBasket = async () => {
            if (!variant) {
                console.error(
                    "Can not add to basket because no variant is selected",
                );
                return;
            }
            setButtonStatus(CartButtonStatus.ADDING);
            await onAddToBasket(variant.url, quantity);
            setButtonStatus(CartButtonStatus.ADDED);
            trackAddToBasketEvent(variant, quantity);
        };

        const cardPortionProps = {
            gridVariant: props.gridVariant,
            cardType: props.cardType,
            rootProduct: props.rootProduct,
            variant,
            hideRatings,
        };

        const cardClass = classNames({
            [GRID_TILE_CLASS]: true, // what MasonryGrid lays tiles out by
            [styles.card]: true,
            [props.cardType]: true,
            [`cardType--${props.rootProduct.id}`]: true,
            [`${props.cardType}--${props.gridVariant}`]: true,
        });
        const contentClass = classNames({
            ["grid-card__content"]: true,
            [`${props.cardType}--${props.gridVariant}__content`]: true,
        });

        if (!shouldShowPricing) {
            return null;
        }

        return (
            <div
                role="group"
                aria-label="product"
                ref={props.forwardedRef}
                className={cardClass}
                onMouseEnter={handleHoverOverlayOpen}
                onMouseLeave={handleHoverOverlayClose}
            >
                {!props.disableHoverOverlay && (
                    <CardHoverOverlay
                        {...cardPortionProps}
                        isOpen={isFocused}
                        isMobile={isMobile.any()}
                        buttonStatus={buttonStatus}
                        onAddToBasket={handleAddToBasket}
                        quantity={quantity}
                    />
                )}
                <PillowSleepBanner {...cardPortionProps} />
                <CardProductLink {...cardPortionProps}>
                    <CardImage {...cardPortionProps} />
                    <CardPromo {...cardPortionProps} />
                </CardProductLink>
                <div className={contentClass}>
                    <CardProductLink {...cardPortionProps}>
                        {props.buildExternalCallout
                            ? props.buildExternalCallout(props.rootProduct)
                            : null}
                        <CardCallout {...cardPortionProps} />
                        <CardTitle {...cardPortionProps} />
                    </CardProductLink>
                    <CardRating {...cardPortionProps} />
                    <CardProductLink {...cardPortionProps}>
                        <CardCopy {...cardPortionProps} />
                    </CardProductLink>
                    <CardDefaultPrice {...cardPortionProps} />
                </div>
            </div>
        );
    },
);
